import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { LinkRouteUrl } from '@/lib/core/utils';
import { useShoppingBagContent } from '@/services/platform/data/shop';
import { getAddresses } from '@/services/platform/data/addresses';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { getMeOrdersV1, getMeShopOrderSummaryV1 } from '@/services/platform/api/me-—-profile/me-—-profile';
import type {
  AddressBookDto,
  OrderSummaryDto,
  ShopOrderConfirmationContentResponseModel,
  ShopOrderSummaryDto,
} from '@/services/platform/api/model';
import type { OrderConfirmationState } from './ShopCheckoutScreen';
import { OrderSummaryAccordionContent } from './OrderSummaryAccordionContent';
import { ShopCheckoutErrorScreen } from './ShopCheckoutErrorScreen';

interface ShopOrderConfirmedScreenProps {
  content: ShopOrderConfirmationContentResponseModel;
}

/**
 * CMS-driven order confirmation page (Figma: Checkout | Order Confirmed).
 * Labels come from the `shopOrderConfirmation` content type; the order data
 * comes solely from the API, never from the cart — the cart stops reflecting
 * the order the moment payment completes, because it *becomes* the order.
 *
 * The order id arrives in navigation state, taken from the payment the customer
 * just completed (`GET /me/checkout/payments/{id}` carries it, and only reports
 * `Succeeded` once the order exists). `getMeOrdersV1` remains only as the
 * recovery path for a reload or a deep link, where navigation state is gone and
 * "your newest order" is the best available guess.
 */
export function ShopOrderConfirmedScreen({ content }: ShopOrderConfirmedScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings, profile } = useApp();
  const { pickupPoints } = useShoppingBagContent();

  const cms = content.properties;
  const state = (location.state ?? undefined) as OrderConfirmationState | undefined;

  /** Resolved from the payment when possible, otherwise recovered from the orders list. */
  const [orderId, setOrderId] = useState<string | undefined>(state?.orderId);
  const [recoveredOrder, setRecoveredOrder] = useState<OrderSummaryDto | undefined>(undefined);
  const [addresses, setAddresses] = useState<AddressBookDto[]>([]);
  const [orderSummary, setOrderSummary] = useState<ShopOrderSummaryDto | undefined>(undefined);
  const [orderResolutionFailed, setOrderResolutionFailed] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Only when the payment did not hand us an id: a reload or a deep link, both of which destroy
  // navigation state. The newest order is a guess, but it is the customer's own newest order and
  // they have just placed one.
  const loadOrder = async () => {
    if (state?.orderId) return;
    try {
      const response = await getMeOrdersV1({ Page: 1, PageSize: 1 });
      const found = response.status === 200 ? (response.data?.items?.[0] ?? undefined) : undefined;
      if (found?.publicGuid) {
        setRecoveredOrder(found);
        setOrderId(found.publicGuid);
        setOrderResolutionFailed(false);
      } else {
        setOrderResolutionFailed(true);
      }
    } catch {
      setOrderResolutionFailed(true);
    }
  };

  useEffect(() => {
    void loadOrder();
  }, [state?.orderId]);

  // Single source of truth for the closed order's financials — no cart fallback.
  const loadSummary = async () => {
    if (!orderId) return;
    try {
      const response = await getMeShopOrderSummaryV1(orderId);
      if (response.status === 200) {
        setOrderSummary(response.data);
        setSummaryFailed(false);
      } else {
        setSummaryFailed(true);
      }
    } catch {
      setSummaryFailed(true);
    }
  };

  useEffect(() => {
    void loadSummary();
  }, [orderId]);

  const hasError = orderResolutionFailed || summaryFailed;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      if (!orderId) {
        await loadOrder();
      } else if (summaryFailed) {
        await loadSummary();
      }
    } finally {
      setRetrying(false);
    }
  };

  const delivery = orderSummary?.delivery;

  // Resolve the delivery address label for the DELIVER TO summary row.
  useEffect(() => {
    if (!delivery?.addressId) return;
    getAddresses(locale ?? undefined).then(data => setAddresses(data ?? []));
  }, [delivery?.addressId, locale]);

  const firstName = profile?.firstName ?? profile?.name?.split(' ')[0] ?? '';
  const currency = orderSummary?.currencyCode ?? recoveredOrder?.currencyCode ?? 'EUR';

  // `state.orderNumber` comes off the payment, so it is already correct before the summary lands.
  const orderNumber =
    orderSummary?.orderNumber ?? state?.orderNumber ?? recoveredOrder?.orderNumber ?? orderId ?? '—';
  const receiptNumber = orderSummary?.receiptNumber ?? orderNumber;
  const orderDateSource = orderSummary?.orderDate ?? recoveredOrder?.created ?? state?.confirmedAt;
  const orderDate = orderDateSource
    ? new Intl.DateTimeFormat(locale ?? 'en-GB', { month: 'short', day: 'numeric', year: 'numeric' }).format(
        new Date(orderDateSource)
      )
    : '—';

  const deliveryAddress = delivery?.addressId
    ? addresses.find(address => address.id === delivery.addressId)
    : undefined;
  const pickupPoint = delivery?.pickupPointId
    ? pickupPoints.find(point => point.id === delivery.pickupPointId)
    : undefined;

  const deliverTo =
    deliveryAddress || pickupPoint
      ? {
          label: deliveryAddress?.label ?? pickupPoint?.properties?.displayName ?? pickupPoint?.name ?? '',
          detail: deliveryAddress
            ? [
                [deliveryAddress.firstName, deliveryAddress.lastName].filter(Boolean).join(' '),
                deliveryAddress.street,
                deliveryAddress.postalCode,
                [deliveryAddress.city, deliveryAddress.country].filter(Boolean).join(' - '),
              ]
                .filter(Boolean)
                .join(', ')
            : [
                pickupPoint?.properties?.addressLine,
                pickupPoint?.properties?.postalCode,
                [pickupPoint?.properties?.city, pickupPoint?.properties?.country].filter(Boolean).join(' - '),
              ]
                .filter(Boolean)
                .join(', '),
        }
      : undefined;

  const goHome = () => {
    const shopHref = LinkRouteUrl(appSettings?.properties?.shop?.route);
    navigate(shopHref ?? '/', { replace: true });
  };

  const goTrackOrder = () => {
    const detailPath = appSettings?.properties?.activityDetail?.route?.path;
    if (!detailPath || !orderId) return;
    navigate(`${detailPath}${detailPath.endsWith('/') ? '' : '/'}${orderId}`, {
      state: { fromPurchase: true },
    });
  };

  if (hasError) {
    return (
      <ShopCheckoutErrorScreen
        headerTitle={t('App.Shop.PaymentTitle')}
        title={t('App.Shop.PaymentErrorTitle')}
        message={t('App.Shop.PaymentErrorMessage')}
        retryLabel={t('App.Shop.RetryPayment')}
        onRetry={handleRetry}
        retrying={retrying}
      />
    );
  }

  return (
    <div className="relative w-full bg-white pb-[calc((5.5rem+env(safe-area-inset-bottom))+160px)]">
      {/* ── Dark hero ──────────────────────────────────────────────────────── */}
      <div className="bg-jncquoi-neutral-1000 pt-safe-area">
        <header className="flex h-13 items-center justify-center px-2">
          <h1 className="font-pp-neue-montreal text-[14px] leading-none font-normal tracking-[0.5px] text-white uppercase">
            {cms?.pageName || content.name || t('App.Shop.OrderConfirmed')}
          </h1>
        </header>
        <div className="flex flex-col gap-4 px-4 pt-4 pb-14 text-center text-white">
          <div className="flex flex-col">
            <p className="font-didot-lt-std text-[50px] leading-normal font-normal tracking-[-2px] italic">
              {cms?.thankYou || t('App.Shop.ThankYou')}
            </p>
            {firstName && (
              <p className="font-pp-neue-montreal text-[20px] leading-normal font-semibold uppercase">
                {firstName}
              </p>
            )}
          </div>
          <p className="font-pp-neue-montreal text-[14px] leading-normal font-normal tracking-[0.5px]">
            {cms?.subTitle || t('App.Shop.OrderReceived')}
          </p>
        </div>
      </div>

      {/* ── Order info ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-8 px-4 pt-6">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-normal font-semibold">
          {t('App.Shop.OrderId')} {orderNumber}
        </p>

        <div className="flex w-full gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-2 px-4">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal font-semibold tracking-[0.5px]">
              {t('App.Shop.ReceiptNumber')}
            </p>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 truncate text-[14px] leading-normal font-normal tracking-[0.5px]">
              {receiptNumber}
            </p>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2 px-4">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal font-semibold tracking-[0.5px]">
              {t('App.Shop.OrderDate')}
            </p>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal font-normal tracking-[0.5px]">
              {orderDate}
            </p>
          </div>
        </div>

        {/* ── Order summary (always expanded, static — no accordion behaviour) ── */}
        <div className="bg-jncquoi-neutral-50">
          <p className="px-4 py-2.5 text-[12px] font-semibold tracking-[0.5px] uppercase">
            {t('App.Shop.OrderSummaryTitle')}
          </p>
          <div className="px-4 pb-4">
            {orderSummary ? (
              <OrderSummaryAccordionContent
                items={orderSummary.items ?? []}
                subtotal={orderSummary.subtotal ?? 0}
                shipping={orderSummary.shipping ?? 0}
                discount={orderSummary.discount ?? 0}
                total={orderSummary.total ?? 0}
                currencyCode={currency}
                locale={locale ?? 'en-GB'}
                giftMessage={orderSummary.gift?.message ?? undefined}
                deliverTo={deliverTo}
              />
            ) : (
              <SpinLoading wrapperClassName="min-h-32" />
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom bar ─────────────────────────────────────────────────────── */}
      {/*
       * Bottom padding clears the floating AppNavBar pill (fixed at
       * `bottom: 1.5rem + safe-area-inset-bottom`, `h-12` tall — see
       * components/layout/navBar/index.tsx) plus a small gap, so the button
       * doesn't render underneath it.
       */}
      <div className="bg-jncquoi-neutral-00 border-jncquoi-neutral-300 fixed right-0 bottom-0 left-0 z-30 border-t-[0.5px] px-4 pt-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
        <div className="flex flex-col gap-2">
          {orderId && (
            <Button
              variant="primary-solid"
              palette="dark"
              size="md"
              className="w-full"
              onClick={goTrackOrder}
            >
              {cms?.trackBtn || t('App.Shop.TrackYourOrder')}
            </Button>
          )}
          <Button variant="tertiary-link" palette="dark" className="w-full uppercase" onClick={goHome}>
            {cms?.continueShoppingBtn || t('App.Shop.ContinueShopping')}
          </Button>
        </div>
      </div>
    </div>
  );
}
